import type {DocsManifestEntry} from '@/types/docsManifest';
import {getRouteSegments} from '@/config/routeSegments';

export const DOC_QUERY_PARAM = 'doc';

const FEATURE_MANUAL_PREFIX = 'user-manual-feature-';

type UserManualSectionKey = 'main' | 'role' | 'feature';
type DocSectionKey = 'technical' | 'test-plan' | UserManualSectionKey;

export interface DocMenuSection {
    key: DocSectionKey;
    labelKey: string;
    entries: DocsManifestEntry[];
}

const DOC_SECTIONS: Array<{key: 'technical' | 'test-plan'; labelKey: string}> = [
    {key: 'technical', labelKey: 'header.technicalDocumentation'},
    {key: 'test-plan', labelKey: 'header.testDocumentation'},
];

const USER_MANUAL_SECTIONS: Array<{key: UserManualSectionKey; labelKey: string}> = [
    {key: 'main', labelKey: 'header.mainUserManual'},
    {key: 'role', labelKey: 'header.roleBasedUserManuals'},
    {key: 'feature', labelKey: 'header.featureUserManuals'},
];

function getUserManualSectionKey(file: string): UserManualSectionKey {
    if (file.startsWith('user-manual-role-')) return 'role';
    if (file.startsWith(FEATURE_MANUAL_PREFIX)) return 'feature';
    return 'main';
}

/**
 * Strip everything but lowercase alphanumerics so kebab-case manual filenames
 * and camelCase route segments compare equal (`my-feature` ⇆ `myFeature`).
 */
function canonicalizeKey(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Canonical feature key for a per-feature user manual, or null for any other
 * doc. `user-manual-feature-my-feature.html` → `myfeature`.
 */
export function getFeatureKeyFromManualFile(file: string): string | null {
    const stem = file.split('/').pop() ?? file;
    if (!stem.startsWith(FEATURE_MANUAL_PREFIX)) return null;
    const feature = stem.slice(FEATURE_MANUAL_PREFIX.length).replace(/\.[^.]+$/, '');
    return feature ? canonicalizeKey(feature) : null;
}

/**
 * Scope per-feature user manuals to the route they document.
 *
 * A feature manual is kept only on the home page (no feature segment) or when a
 * segment of the current route matches its feature — so it shows on its own page
 * and every sub-page. Every other doc (technical, test plan, main and
 * role-based manuals) is always kept.
 */
export function filterManualsForRoute(entries: DocsManifestEntry[], pathname: string): DocsManifestEntry[] {
    const segmentKeys = new Set(getRouteSegments(pathname).map(canonicalizeKey));
    const isHome = segmentKeys.size === 0;

    return entries.filter(entry => {
        const featureKey = getFeatureKeyFromManualFile(entry.file);
        if (featureKey === null) {
            return true;
        }
        return isHome || segmentKeys.has(featureKey);
    });
}

export function normalizeDocQueryValue(value: string): string {
    try {
        const url = new URL(value, window.location.origin);
        const pathSegments = url.pathname.split('/').filter(Boolean);
        return pathSegments[pathSegments.length - 1] ?? value;
    } catch {
        const pathSegments = value.split(/[\\/]/).filter(Boolean);
        return pathSegments[pathSegments.length - 1] ?? value;
    }
}

export function resolveDocFromQuery(entries: DocsManifestEntry[], value: string | null): DocsManifestEntry | null {
    if (!value) return null;
    const normalized = normalizeDocQueryValue(value);
    return entries.find(entry => entry.file === normalized) ?? null;
}

export function buildViewerUrl(file: string): string {
    const url = new URL(window.location.href);
    url.searchParams.set(DOC_QUERY_PARAM, file);
    return url.toString();
}

export function buildDocAssetUrl(file: string): string {
    return `/docs/${file}`;
}

export function updateViewerUrl(file: string | null) {
    const url = new URL(window.location.href);
    if (file) {
        url.searchParams.set(DOC_QUERY_PARAM, file);
    } else {
        url.searchParams.delete(DOC_QUERY_PARAM);
    }
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

export async function copyText(value: string) {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = value;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'absolute';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
}

export function openDocInNewTab(file: string) {
    window.open(buildDocAssetUrl(file), '_blank', 'noopener,noreferrer');
}

export function downloadDocFile(file: string) {
    const link = document.createElement('a');
    link.href = buildDocAssetUrl(file);
    link.download = file;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export function printFrameWindow(frameWindow: Window | null | undefined): boolean {
    if (!frameWindow) return false;
    frameWindow.focus();
    frameWindow.print();
    return true;
}

export function printPdfFile(file: string, onFallback: () => void) {
    const printWindow = window.open(buildDocAssetUrl(file), '_blank');
    if (!printWindow) {
        onFallback();
        return;
    }

    const triggerPrint = () => {
        try {
            if (!printFrameWindow(printWindow)) {
                throw new Error('print unavailable');
            }
        } catch {
            onFallback();
        }
    };

    const timeoutId = window.setTimeout(triggerPrint, 750);

    try {
        printWindow.addEventListener('load', () => {
            window.clearTimeout(timeoutId);
            triggerPrint();
        }, {once: true});
    } catch {
        // Some browser PDF viewers may not expose a normal load event.
    }

    try {
        printWindow.addEventListener('afterprint', () => {
            printWindow.close();
        }, {once: true});
    } catch {
        // Ignore browsers that do not expose afterprint on the opened window.
    }
}

export function buildDocMenuSections(entries: DocsManifestEntry[]): DocMenuSection[] {
    const sections: DocMenuSection[] = [];

    for (const section of DOC_SECTIONS) {
        const sectionEntries = entries.filter(entry => entry.type === section.key);
        if (sectionEntries.length > 0) {
            sections.push({...section, entries: sectionEntries});
        }
    }

    const userManualEntries = entries.filter(entry => entry.type === 'user-manual');
    for (const section of USER_MANUAL_SECTIONS) {
        const sectionEntries = userManualEntries.filter(entry => getUserManualSectionKey(entry.file) === section.key);
        if (sectionEntries.length > 0) {
            sections.push({...section, entries: sectionEntries});
        }
    }

    return sections;
}
